import React, { useState, useEffect } from 'react';
import { Plus, BarChart3, TrendingUp, Activity, Eye, EyeOff } from 'lucide-react';
import { Goal, Metric, MetricForecast } from '../../types';
import { apiService } from '../../services/api';
import { CreateMetricModal } from './CreateMetricModal';
import { RecordMetricValueModal } from './RecordMetricValueModal';
import { MetricForecastChart } from './MetricForecastChart';
import { WhatIfScenario } from './WhatIfScenario';
import { MetricAlerts } from './MetricAlerts';

interface EnhancedGoalMetricsProps {
  goal: Goal;
  onGoalUpdated?: () => void;
}

export const EnhancedGoalMetrics: React.FC<EnhancedGoalMetricsProps> = ({ goal, onGoalUpdated }) => {
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [forecasts, setForecasts] = useState<Record<string, MetricForecast>>({});
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRecordModal, setShowRecordModal] = useState<Metric | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<Metric | null>(null);
  const [activeView, setActiveView] = useState<'overview' | 'forecast' | 'scenarios' | 'alerts'>('overview');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMetrics();
  }, [goal.id]);

  useEffect(() => {
    if (metrics.length > 0) {
      fetchForecasts();
    }
  }, [metrics]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const response = await apiService.getMetrics(goal.id);
      setMetrics(response.metrics);
      
      // Set first metric as selected if none selected
      if (response.metrics.length > 0 && !selectedMetric) {
        setSelectedMetric(response.metrics[0]);
      }
    } catch (err) {
      console.error('Failed to fetch metrics:', err);
      setError('Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  const fetchForecasts = async () => {
    try {
      const forecastPromises = metrics.map(async (metric) => {
        try {
          const response = await apiService.getMetricForecast(metric.id);
          return { metricId: metric.id, forecast: response.forecast };
        } catch (err) {
          console.warn(`Failed to fetch forecast for metric ${metric.id}:`, err);
          return { metricId: metric.id, forecast: null };
        }
      });

      const results = await Promise.all(forecastPromises);
      const forecastMap = results.reduce((acc, result) => {
        if (result.forecast) {
          acc[result.metricId] = result.forecast;
        }
        return acc;
      }, {} as Record<string, MetricForecast>);

      setForecasts(forecastMap);
    } catch (err) {
      console.error('Failed to fetch forecasts:', err);
    }
  };

  const handleMetricCreated = () => {
    setShowCreateModal(false);
    fetchMetrics();
    if (onGoalUpdated) {
      onGoalUpdated();
    }
  };

  const handleValueRecorded = () => {
    setShowRecordModal(null);
    fetchMetrics();
    fetchForecasts();
    if (onGoalUpdated) {
      onGoalUpdated();
    }
  };

  const handleDeleteMetric = async (metricId: string) => {
    if (!window.confirm('Are you sure you want to delete this metric?')) {
      return;
    }

    try {
      await apiService.deleteMetric(metricId);
      setMetrics(metrics.filter(m => m.id !== metricId));
      
      // Update selected metric if deleted
      if (selectedMetric?.id === metricId) {
        const remaining = metrics.filter(m => m.id !== metricId);
        setSelectedMetric(remaining.length > 0 ? remaining[0] : null);
      }
      
      if (onGoalUpdated) {
        onGoalUpdated();
      }
    } catch (err) {
      console.error('Failed to delete metric:', err);
      setError('Failed to delete metric');
    }
  };

  const getMetricTypeIcon = (type: string) => {
    switch (type) {
      case 'rate':
        return <TrendingUp className="w-4 h-4 text-blue-500" />;
      case 'binary':
        return <Eye className="w-4 h-4 text-green-500" />;
      default:
        return <BarChart3 className="w-4 h-4 text-purple-500" />;
    }
  };

  const formatMetricType = (type: string) => {
    switch (type) {
      case 'absolute':
        return 'Absolute';
      case 'rate':
        return 'Rate';
      case 'binary':
        return 'Binary';
      default:
        return type;
    }
  };

  const getProgressColor = (current: number, target?: number) => {
    if (!target) return 'bg-gray-400';
    
    const percentage = (current / target) * 100;
    if (percentage >= 100) return 'bg-green-500';
    if (percentage >= 75) return 'bg-blue-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const calculateProgress = (current: number, target?: number) => {
    if (!target) return 0;
    return Math.min((current / target) * 100, 100);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Metrics & Forecasting
            <span className="text-sm font-normal text-gray-500">
              ({metrics.length})
            </span>
          </h3>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-secondary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Metric
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {metrics.length === 0 ? (
          <div className="text-center py-8">
            <BarChart3 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">No metrics yet</h4>
            <p className="text-gray-500 mb-6">
              Add metrics to track progress and get forecasting insights
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              Create Your First Metric
            </button>
          </div>
        ) : (
          <>
            {/* Metrics Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {metrics.map((metric) => (
                <div
                  key={metric.id}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    selectedMetric?.id === metric.id
                      ? 'border-blue-500 bg-blue-50'
                      : metric.isPrimary 
                        ? 'border-blue-200 bg-blue-25' 
                        : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedMetric(metric)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3 flex-1">
                      {getMetricTypeIcon(metric.metricType)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-gray-900 truncate">{metric.name}</h4>
                          {metric.isPrimary && (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              Primary
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span className="px-2 py-1 rounded-full bg-gray-100">
                            {formatMetricType(metric.metricType)}
                          </span>
                          <span>Weight: {metric.weight}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowRecordModal(metric);
                        }}
                        className="p-1 text-gray-400 hover:text-blue-600"
                        title="Record new value"
                      >
                        <Activity className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteMetric(metric.id);
                        }}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Delete metric"
                      >
                        <EyeOff className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <div className="text-2xl font-bold text-gray-900">
                        {metric.currentValue}
                        {metric.unit && <span className="text-sm font-normal"> {metric.unit}</span>}
                      </div>
                      {metric.lastUpdate && (
                        <p className="text-xs text-gray-500">
                          Updated: {new Date(metric.lastUpdate).toLocaleDateString()}
                        </p>
                      )}
                    </div>

                    {metric.targetValue && (
                      <div>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-600">Progress to Target</span>
                          <span className="font-medium">
                            {Math.round(calculateProgress(metric.currentValue, metric.targetValue))}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(metric.currentValue, metric.targetValue)}`}
                            style={{ width: `${calculateProgress(metric.currentValue, metric.targetValue)}%` }}
                          />
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Target: {metric.targetValue} {metric.unit}
                          {metric.targetDate && (
                            <span> by {new Date(metric.targetDate).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Selected Metric Analysis */}
            {selectedMetric && (
              <>
                {/* View Tabs */}
                <div className="flex items-center bg-gray-100 rounded-lg p-1 mb-6">
                  {[
                    { id: 'overview', label: 'Overview', icon: <BarChart3 className="w-4 h-4" /> },
                    { id: 'forecast', label: 'Forecast', icon: <TrendingUp className="w-4 h-4" /> },
                    { id: 'scenarios', label: 'What-If', icon: <Activity className="w-4 h-4" /> },
                    { id: 'alerts', label: 'Alerts', icon: <Eye className="w-4 h-4" /> }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveView(tab.id as any)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        activeView === tab.id
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {tab.icon}
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Active View Content */}
                <div className="space-y-6">
                  {activeView === 'overview' && (
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">
                        {selectedMetric.name} - Overview
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div className="bg-gray-50 p-3 rounded">
                          <div className="text-gray-600 mb-1">Current Value</div>
                          <div className="text-xl font-bold">{selectedMetric.currentValue} {selectedMetric.unit}</div>
                        </div>
                        {selectedMetric.targetValue && (
                          <div className="bg-gray-50 p-3 rounded">
                            <div className="text-gray-600 mb-1">Target Value</div>
                            <div className="text-xl font-bold">{selectedMetric.targetValue} {selectedMetric.unit}</div>
                          </div>
                        )}
                        <div className="bg-gray-50 p-3 rounded">
                          <div className="text-gray-600 mb-1">Metric Type</div>
                          <div className="font-semibold">{formatMetricType(selectedMetric.metricType)}</div>
                        </div>
                        <div className="bg-gray-50 p-3 rounded">
                          <div className="text-gray-600 mb-1">Weight</div>
                          <div className="font-semibold">{selectedMetric.weight}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeView === 'forecast' && (
                    <MetricForecastChart metric={selectedMetric} />
                  )}

                  {activeView === 'scenarios' && (
                    <WhatIfScenario 
                      metric={selectedMetric} 
                      currentForecast={forecasts[selectedMetric.id] || null} 
                    />
                  )}

                  {activeView === 'alerts' && (
                    <MetricAlerts 
                      metric={selectedMetric} 
                      forecast={forecasts[selectedMetric.id] || null} 
                    />
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateMetricModal
          goalId={goal.id}
          goalName={goal.name}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleMetricCreated}
        />
      )}

      {showRecordModal && (
        <RecordMetricValueModal
          metric={showRecordModal}
          onClose={() => setShowRecordModal(null)}
          onSuccess={handleValueRecorded}
        />
      )}
    </div>
  );
};